import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../../services/api';
import useOptimisticMutation from '../useOptimisticMutation';
import useToaster from '../useToaster';
import useAccount from './useAccount';

const useEmployeeComments = ({ employeeId }) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const storeKey = ['comments', accountId, employeeId];
  const queryClient = useQueryClient();
  const { addSuccess, addResponseError } = useToaster();

  const { isLoading, data } = useQuery(storeKey, () => api.fetchEmployeeComments({ accountId, employeeId }));

  const { mutateAsync: deleteComment } = useOptimisticMutation(
    storeKey,
    ({ id }) => api.deleteEmployeeComment({ accountId, employeeId, id }),
    ({ previousData, id }) => {
      return {
        data: (previousData?.data || []).filter(({ id: currentId }) => currentId !== id)
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
        addSuccess(t('successDeleteComment'));
      },
      onError: (err) => {
        addResponseError(err);
      }
    }
  );

  const { mutateAsync: addComment } = useMutation(
    ({ comment, typeId, filePath, attachmentName }) =>
      api.addEmployeeComment({ accountId, employeeId, comment, typeId, filePath, attachmentName }),
    {
      throwOnError: true,
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
        addSuccess(t('successAddingComment'));
      },
      onError: (err) => {
        addResponseError(err);
      }
    }
  );

  const { mutateAsync: sendCommentEmail } = useMutation(
    ({ commentId }) => api.sendCommentAttachmentEmail({ accountId, employeeId, commentId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
        addSuccess(t('successSendingCommentEmail'));
      },
      onError: (err) => {
        addResponseError(err);
      }
    }
  );

  return {
    isLoading,
    data: data?.data || [],
    deleteComment,
    addComment,
    sendCommentEmail
  };
};

export default useEmployeeComments;
